import { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { CheckCircle, XCircle, ChevronRight, Target } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useConfetti } from '../../../hooks/cabin/useConfetti';

// Softer shake — 3 frames, shorter duration
const shakeVariants = {
  shake: {
    x: [-5, 5, -4, 4, 0],
    transition: { duration: 0.32 },
  },
  idle: { x: 0 },
};

/**
 * Resolves question text from resolved-i18n backend (plain string) or legacy mock schema.
 * Backend (new): question is a plain string
 * Legacy mock: question_tr / question_en
 */
function resolveQuestion(q, lang) {
  if (!q) return '';
  if (typeof q.question === 'string' && q.question) return q.question;
  return lang === 'tr'
    ? (q.question_tr || q.question_en || '')
    : (q.question_en || q.question_tr || '');
}

/**
 * Resolves option text from resolved-i18n backend or legacy mock schema.
 * Backend (new): text is a plain string
 * Legacy mock: text_tr / text_en
 */
function resolveOptionText(opt, lang) {
  if (!opt) return '';
  if (typeof opt.text === 'string' && opt.text) return opt.text;
  return lang === 'tr'
    ? (opt.text_tr || opt.text_en || '')
    : (opt.text_en || opt.text_tr || '');
}

/**
 * Resolves feedback/explanation text.
 * Backend (new): feedback_tr / feedback_en on each option
 * Legacy mock: explanation_tr / explanation_en on the question
 */
function resolveExplanation(q, selectedIdx, lang) {
  if (!q) return '';
  const selectedOpt = q.options?.[selectedIdx];
  // New schema: feedback on the selected option
  if (selectedOpt) {
    const optFeedback =
      lang === 'tr'
        ? (selectedOpt.feedback_tr || selectedOpt.feedback_en || '')
        : (selectedOpt.feedback_en || selectedOpt.feedback_tr || '');
    if (optFeedback) return optFeedback;
  }
  // Legacy mock: explanation on the question
  return lang === 'tr'
    ? (q.explanation_tr || q.explanation_en || '')
    : (q.explanation_en || q.explanation_tr || '');
}

/**
 * Determines if the selected option is correct.
 * Backend (new): opt.is_correct boolean
 * Legacy mock: selectedId === currentQ.correct_id
 */
function isOptionCorrect(opt, optIdx, currentQ) {
  if (!opt) return false;
  // New schema: is_correct boolean
  if (typeof opt.is_correct === 'boolean') return opt.is_correct;
  // Legacy mock: match against correct_id
  if (currentQ.correct_id !== undefined) {
    return opt.id !== undefined
      ? opt.id === currentQ.correct_id
      : optIdx === currentQ.correct_id;
  }
  return false;
}

export default function MultipleChoice({
  phase,
  onPhaseComplete,
  checkAnswer,
  'data-testid': testId = 'multiple-choice',
}) {
  const { t, lang } = useLanguage();
  const { play } = useSoundEffects();
  const { fireSmall } = useConfetti();
  const shouldReduce = useReducedMotion();

  const questions = phase?.content?.questions || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  // selectedIndex is the array index of the chosen option
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [shakeTrigger, setShakeTrigger] = useState(false);
  // Server-side grading (Bug 3): toplanan secimler + bu soru icin server sonucu
  const collectedAnswers = useRef([]);
  const [serverResult, setServerResult] = useState(null); // { is_correct, correct_answer }
  // checking: true while awaiting server check-answer response (suppress color flash)
  const [checking, setChecking] = useState(false);

  const currentQ = questions[currentIndex];
  const isLast = currentIndex === questions.length - 1;
  const maxXP = phase?.xp_in_phase || 0;

  const questionText = resolveQuestion(currentQ, lang);

  const handleSelect = useCallback(
    (idx) => {
      if (submitted) return;
      setSelectedIndex(idx);
      play('click');
    },
    [submitted, play]
  );

  const handleSubmit = useCallback(async () => {
    if (selectedIndex === null || submitted) return;
    setSubmitted(true);

    // Kullanicinin sectigi cevabi yapilandirilmis payloada ekle (server grading)
    collectedAnswers.current = [
      ...collectedAnswers.current,
      { question_index: currentIndex, selected_index: selectedIndex },
    ];

    // Dogruluk: server varsa otoriter, yoksa local fallback (test/legacy)
    let correct;
    if (typeof checkAnswer === 'function') {
      // Renk flash'ini onlemek icin server yaniti gelene kadar checking=true tut
      setChecking(true);
      const res = await checkAnswer({
        phaseId: phase?.phase_id,
        questionIndex: currentIndex,
        answer: { selected_index: selectedIndex },
      });
      setChecking(false);
      if (res) {
        setServerResult(res);
        correct = !!res.is_correct;
      } else {
        // ag hatasi → local fallback
        correct = isOptionCorrect(currentQ?.options?.[selectedIndex], selectedIndex, currentQ);
      }
    } else {
      correct = isOptionCorrect(currentQ?.options?.[selectedIndex], selectedIndex, currentQ);
    }

    if (correct) {
      play('correct');
      if (!shouldReduce) {
        fireSmall({ y: 0.5 });
      }
      setCorrectCount((c) => c + 1);
    } else {
      play('wrong');
      if (!shouldReduce) {
        setShakeTrigger(true);
        setTimeout(() => setShakeTrigger(false), 400);
      }
    }
  }, [selectedIndex, submitted, currentQ, currentIndex, checkAnswer, phase, play, shouldReduce, fireSmall]);

  const handleNext = useCallback(() => {
    if (isLast) {
      const score =
        questions.length > 0
          ? Math.round((correctCount / questions.length) * maxXP)
          : 0;
      onPhaseComplete({
        score, // server submit-phase yeniden hesaplar; bu yalnizca local UI/fallback
        maxScore: maxXP,
        answers: collectedAnswers.current, // [{question_index, selected_index}]
        timeSpent: 0,
      });
    } else {
      setCurrentIndex((i) => i + 1);
      setSelectedIndex(null);
      setSubmitted(false);
      setServerResult(null);
      setChecking(false);
    }
  }, [isLast, correctCount, questions.length, maxXP, onPhaseComplete]);

  if (!currentQ) return null;

  const selectedOpt = currentQ.options?.[selectedIndex];
  // checking=true means we're awaiting server; don't apply color until server responds
  const submittedCorrect =
    submitted && !checking && (
      serverResult
        ? !!serverResult.is_correct
        : (selectedIndex !== null && isOptionCorrect(selectedOpt, selectedIndex, currentQ))
    );
  // Server modunda dogru option index (yanlissa vurgulamak icin)
  const serverCorrectIndex =
    serverResult && typeof serverResult.correct_answer === 'number'
      ? serverResult.correct_answer
      : null;

  const explanationText = resolveExplanation(currentQ, selectedIndex, lang);

  const counterText = t('exercise.question_counter')
    ? t('exercise.question_counter')
        .replace('{current}', currentIndex + 1)
        .replace('{total}', questions.length)
    : `Soru ${currentIndex + 1} / ${questions.length}`;

  const maxXpText = t('exercise.max_xp')
    ? t('exercise.max_xp').replace('{xp}', maxXP)
    : `Maks. ${maxXP} XP`;

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-2xl mx-auto"
      data-testid={testId}
    >
      {/* ── Gradient question header card ──────────────────────────────── */}
      <motion.div
        key={currentIndex}
        initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-5 shadow-[0_4px_0_0_#c2410c] text-white"
      >
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-xl">
              <Target size={18} className="text-white" aria-hidden="true" />
            </div>
            <span className="text-xs uppercase tracking-wide font-black text-white/90">
              {t('exercise.mini_question') || 'Mini Soru'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-white/80" data-testid={`${testId}-progress`}>
              {counterText}
            </span>
            {maxXP > 0 && (
              <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-lg">
                {maxXpText}
              </span>
            )}
          </div>
        </div>
        {/* Question text */}
        <p
          className="text-lg md:text-xl font-bold text-white leading-snug"
          data-testid={`${testId}-question`}
        >
          {questionText}
        </p>
      </motion.div>

      {/* ── Options ─────────────────────────────────────────────────────── */}
      <motion.div
        role="radiogroup"
        aria-label={questionText}
        variants={shouldReduce ? {} : shakeVariants}
        animate={shakeTrigger ? 'shake' : 'idle'}
        className="flex flex-col gap-3"
      >
        {(currentQ.options || []).map((opt, idx) => {
          const optText = resolveOptionText(opt, lang);
          const isSelected = selectedIndex === idx;
          // While checking (awaiting server), suppress correct/wrong colors entirely.
          // Server modunda dogru option index serverCorrectIndex ile gelir;
          // legacy/test modunda option.is_correct/correct_id kullanilir.
          const isOptCorrect = submitted && !checking && (
            serverCorrectIndex !== null
              ? idx === serverCorrectIndex
              : isOptionCorrect(opt, idx, currentQ)
          );
          const isOptWrong = submitted && !checking && isSelected && !isOptCorrect;

          let borderClass = 'border-2 border-[var(--border-light)] bg-white hover:border-[var(--brand-primary)] hover:bg-sky-50';
          let textClass = 'text-[var(--text-main)]';

          if (isOptCorrect) {
            borderClass = 'border-2 border-[#10B981] bg-[#10B981]/10';
            textClass = 'text-emerald-700 font-bold';
          } else if (isOptWrong) {
            borderClass = 'border-2 border-[var(--brand-danger)] bg-[var(--brand-danger)]/10';
            textClass = 'text-red-700 font-bold';
          } else if (isSelected && !submitted) {
            borderClass = 'border-2 border-[var(--brand-primary)] bg-sky-50';
            textClass = 'text-sky-700 font-semibold';
          }

          const optKey = opt.id !== undefined ? opt.id : idx;

          return (
            <motion.button
              key={optKey}
              role="radio"
              aria-checked={isSelected}
              aria-disabled={submitted}
              whileHover={submitted || shouldReduce ? {} : { scale: 1.01 }}
              whileTap={submitted || shouldReduce ? {} : { scale: 0.99 }}
              onClick={() => handleSelect(idx)}
              disabled={submitted}
              className={`w-full flex items-center gap-3 px-4 py-4 rounded-xl text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] disabled:cursor-default min-h-[52px] ${borderClass}`}
              data-testid={`${testId}-option-${idx}`}
            >
              {/* Radio indicator */}
              <span
                className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
                  ${isOptCorrect ? 'border-[#10B981] bg-[#10B981]' : ''}
                  ${isOptWrong ? 'border-[var(--brand-danger)] bg-[var(--brand-danger)]' : ''}
                  ${isSelected && !submitted ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]' : ''}
                  ${!isSelected && !isOptCorrect && !isOptWrong ? 'border-gray-300' : ''}
                `}
                aria-hidden="true"
              >
                {isOptCorrect && <CheckCircle size={12} className="text-white" />}
                {isOptWrong && <XCircle size={12} className="text-white" />}
                {isSelected && !submitted && (
                  <span className="w-2 h-2 rounded-full bg-white" />
                )}
              </span>
              <span className={`text-sm flex-1 ${textClass}`}>{optText}</span>
            </motion.button>
          );
        })}
      </motion.div>

      {/* ── Feedback — only shown after server responds (checking=false) ── */}
      <AnimatePresence>
        {submitted && !checking && (
          <motion.div
            key="feedback"
            initial={shouldReduce ? {} : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
          >
            <div
              className={`flex items-start gap-3 p-4 rounded-xl border-2 ${
                submittedCorrect
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-red-200 bg-red-50'
              }`}
              role="alert"
              data-testid={`${testId}-feedback`}
            >
              {submittedCorrect ? (
                <CheckCircle
                  size={20}
                  className="text-emerald-500 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
              ) : (
                <XCircle
                  size={20}
                  className="text-red-500 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
              )}
              <div>
                <p
                  className={`text-sm font-bold mb-1 ${
                    submittedCorrect ? 'text-emerald-700' : 'text-red-700'
                  }`}
                >
                  {submittedCorrect
                    ? t('exercise.correct') || 'Doğru!'
                    : t('exercise.wrong') || 'Yanlış!'}
                </p>
                {explanationText && (
                  <p className="text-sm text-[var(--text-muted)]">{explanationText}</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Action buttons — centered ────────────────────────────────────── */}
      <div className="flex justify-center pt-1">
        {!submitted ? (
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.01 }}
            whileTap={shouldReduce ? {} : { scale: 0.98 }}
            onClick={handleSubmit}
            disabled={selectedIndex === null}
            className="flex items-center gap-2 px-10 py-4 bg-[var(--brand-primary)] text-white font-black text-base rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            data-testid={`${testId}-submit`}
          >
            {t('exercise.submit') || 'Kontrol Et'}
          </motion.button>
        ) : checking ? (
          /* Awaiting server grading — neutral loading state, no "Next" yet */
          <div
            className="flex items-center gap-2 px-10 py-4 bg-gray-200 text-gray-400 font-black text-base rounded-xl cursor-wait"
            data-testid={`${testId}-checking`}
            aria-busy="true"
            aria-label={t('exercise.checking') || 'Kontrol ediliyor...'}
          >
            <span className="w-4 h-4 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" aria-hidden="true" />
            {t('exercise.checking') || 'Kontrol ediliyor...'}
          </div>
        ) : (
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.01 }}
            whileTap={shouldReduce ? {} : { scale: 0.98 }}
            onClick={handleNext}
            className="flex items-center gap-2 px-10 py-4 bg-[var(--brand-primary)] text-white font-black text-base rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            data-testid={`${testId}-next`}
          >
            {isLast
              ? t('exercise.finish_lesson') || 'Dersi Tamamla'
              : t('exercise.next') || 'Sonraki Soru'}
            <ChevronRight size={20} aria-hidden="true" />
          </motion.button>
        )}
      </div>
    </div>
  );
}
